import {Bonus} from 'com/gongxigames/math/core/game/engine/bonus/Bonus';
import {Pay} from 'com/gongxigames/math/core/game/Pay';
import {
    SpinButtonBaseViewMobile, SpinButtonBasicState, SpinButtonEvents,
    SpinButtonView, SpinButtonViewEvents,
    SpinButtonViewMobile, SpinButtonViewMobileLandscape, SpinButtonWhileSpinningState
} from '@gongxigames/framework/slot/modules/spinbutton';
import {GlobalEventsMapForBackwardCompatibility, UIButtonsChangeEnableEvent} from '@gongxigames/framework/slot/events';
import {consoleLogger, ReasonID} from '@gongxigames/framework/core';
import {LogicEvents} from '@gongxigames/framework/slot/services/logic/LogicEvents';
import {ReelsEvents} from '@gongxigames/framework/slot/modules/reels';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow';
import {WinPresentationEvents} from '@gongxigames/framework/slot/modules/winpresentation';
import {AutoplayEvents} from '@gongxigames/framework/slot/modules/autoplay';
import {GenericPopupEvents, GenericPopupType} from '@gongxigames/framework/slot/modules/genericPopup';
import {UIDisableReason} from '@gongxigames/framework/slot/modules/keypad';
import {KeypadControlsEvents} from '@gongxigames/framework/slot/modules/keypadControls/KeypadControlsEvents';
import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {UIButtons} from '@gongxigames/framework/slot/types/UIButtons';

type View = SpinButtonView | SpinButtonViewMobile;

export class SpinButtonModule extends Module<View, GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol('SpinButtonModule');

    public static readonly BASIC_STATE: string = 'basic';
    public static readonly REELS_SPINNING_STATE: string = 'spinning';
    public roundDelay = 0;
    public quickStopDisabled = false;

    private _disabledButtonBy: symbol[] = [];
    private _disabledHoldBy: symbol[] = [];
    private _disabledHoldSettings = true;
    private readonly _holdEnableDisableReasonSpin: symbol = Symbol('spin');

    public init(): void {
        super.init();

        this.view.initMainGame();

        this.quickStopDisabled = this._settings.get('quickStopDisabled');
        this.roundDelay = this.settings.get('roundDelay');

        if (this._settings.get('autoSpinDisabled')) {
            if (this.view instanceof SpinButtonViewMobile) {
                this.view.setSprite('spinButtonWithoutHoldSettings');
            }

            this.disableHold(Symbol('forever'));
        }
    }

    public enableButton(reason: symbol): void {
        const index = this._disabledButtonBy.indexOf(reason);

        if (index !== -1) {
            this._disabledButtonBy.splice(index, 1);

            if (!this._disabledButtonBy.length) {
                this.view.enableSpinButton();
            }
        }

        this.logDisableBy();
    }

    public enableHold(reason: symbol): void {
        const index = this._disabledHoldBy.indexOf(reason);

        if (index !== -1) {
            this._disabledHoldBy.splice(index, 1);

            if (!this._disabledHoldBy.length) {
                if (this.view instanceof SpinButtonViewMobile) {
                    this.view.enableHold();
                }
            }
        }
    }

    public disableButton(reason: symbol): void {
        if (this._disabledButtonBy.indexOf(reason) === -1) {
            this._disabledButtonBy.push(reason);
            this.view.disableSpinButton();
        }

        this.logDisableBy();
    }

    protected logDisableBy(): void {
        let reasons = ' NONE';

        if (this._disabledButtonBy && this._disabledButtonBy.length > 0) {
            reasons = '\n';

            this._disabledButtonBy.forEach((reason) => {
                reasons += ('- ' + reason.toString() + '\n');
            });
        }

        consoleLogger.log(`DISABLE REASONS:${reasons}`);
    }

    public disableHold(reason: symbol): void {
        if (this._disabledHoldBy.indexOf(reason) === -1) {
            this._disabledHoldBy.push(reason);

            if (this.view instanceof SpinButtonViewMobile) {
                this.view.disableHold();
            }
        }
    }

    protected setupViews(): void {
        if (this.isPlatformDesktop()) {
            this.setView(SpinButtonView.NAME);
        } else {
            this.setView(SpinButtonViewMobile.NAME);
        }
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.onCurrentStateHandle(ReelsEvents.SPIN_ENDED);
        this.onCurrentStateHandle(ReelsEvents.REELS_SPINNING_STARTED);
        this.onCurrentStateHandle(KeypadControlsEvents.TOTAL_BET_CHANGED);
        this.onCurrentStateHandle(FlowModuleEvents.HAS_RESPIN_ON_NEXT_SPIN);
        this.onCurrentStateHandle(FlowModuleEvents.NO_RESPIN_ON_NEXT_SPIN);

        this.on(FlowModuleEvents.FREESPINS_RESTORED, this.onFreeSpinsRestored);
        this.on(FlowModuleEvents.RESPIN_RESTORED, this.onRespinRestored);

        this.on('General.Game.STARTED', this.onGameStarted);
        this.on(ReelsEvents.SPIN_ENDED, this.onSpinEnded);
        this.on(ReelsEvents.REELS_SPINNING_STARTED, this.onSpinStarted);
        this.on(WinPresentationEvents.INITIAL_ANIMATION_STARTED, this.onInitialWinAnimationStarted);

        this.on(AutoplayEvents.STOP_AUTOPLAY, this.onAutoplayStopped);
        this.on(AutoplayEvents.START_AUTOPLAY, this.onAutoplayStarted);

        this.on(LogicEvents.SPIN_RESPONSE, this.onSpinResponse);
        this.on(LogicEvents.RESPIN_RESPONSE, this.onRespinResponse);
        this.on(LogicEvents.FREE_SPIN_RESPONSE, this.onFreeSpinResponse);
        this.on(LogicEvents.MINI_SPIN_RESPONSE, this.onMiniSpinResponse);

        this.on(SpinButtonEvents.ENABLE, this.spinButtonEnableListener);
        this.on(SpinButtonEvents.DISABLE, this.spinButtonDisableListener);
        this.on('General.UI.ENABLE', this.uiEnableListener);
        this.on('General.UI.DISABLE', this.uiDisableListener);
        this.on('General.UI.Button.ENABLE', this.onEnableUIButton);
        this.on('General.UI.Button.DISABLE', this.onDisableUIButton);
        this.on(GenericPopupEvents.INTRO_STARTED, this.onPopupStarted);
        this.on(GenericPopupEvents.POPUP_CLOSED, this.onPopupClosed);

        this.onView(SpinButtonViewEvents.SPIN_BUTTON_CLICKED, this.onSpinButtonClicked);
        this.onView(SpinButtonViewEvents.SPIN_BUTTON_HOLD, this.onSpinButtonHold);
        this.onView(SpinButtonBaseViewMobile.SPINS_LEFT_BUTTON_CLICKED, this.onSpinsLeftButtonCLicked);
    }

    protected onPopupStarted(id: symbol, type: symbol): void {
        if (type === GenericPopupType.RESPIN_INTRO) {
            this.onRespinStarted(true);
        }

        this.currentState.handle(id as any, type);
    }

    protected onPopupClosed(id: symbol, type: symbol): void {
        if (type === GenericPopupType.FREESPIN_INTRO) {
            this.onFreeSpinsIntroFinished();
        }

        this.currentState.handle(id as any, type);
    }

    protected setupStates(): void {
        this.addState(new SpinButtonBasicState(this, SpinButtonModule.BASIC_STATE));
        this.addState(new SpinButtonWhileSpinningState(this, SpinButtonModule.REELS_SPINNING_STATE));

        this.gotoState(SpinButtonModule.BASIC_STATE);
    }

    protected spinButtonEnableListener(_eventID: string | symbol, reason: ReasonID): void {
        this.enableButton(reason);
    }

    protected spinButtonDisableListener(eventID: string | symbol, reason: symbol): void {
        this.disableButton(reason);
    }

    protected uiEnableListener(_eventID: string | symbol, reason: ReasonID): void {
        this.enableButton(reason);
        this.enableHold(reason);
    }

    protected uiDisableListener(eventID: string | symbol, reason: symbol): void {
        this.disableButton(reason);
        this.disableHold(reason);
    }

    protected onEnableUIButton(eventID: string, event: UIButtonsChangeEnableEvent): void {
        if (event.name === UIButtons.AUTOPLAY) {
            if(this._settings.get('autoSpinDisabled')){
                return;
            }

            if (this.view instanceof SpinButtonViewMobile) {
                this.view.setSprite('spinButton');
            }

            this.enableHold(event.reason);
        }
    }

    protected onDisableUIButton(eventID: string, event: UIButtonsChangeEnableEvent): void {
        if (event.name === UIButtons.AUTOPLAY) {
            if(this._settings.get('autoSpinDisabled')){
                return;
            }

            if (this.view instanceof SpinButtonViewMobile) {
                this.view.setSprite('spinButtonWithoutHoldSettings');
            }

            this.disableHold(event.reason);
        }
    }

    protected onGameStarted(): void {
        this.eventManager.on('General.UI.SPACEBAR_UP', this.onSpaceBarUp.bind(this), this, 999);
    }

    protected onSpinStarted(): void {
        this.view.onSpinStarted();
        this.disableHold(this._holdEnableDisableReasonSpin);
    }

    protected onSpinEnded(): void {
        this.enableHold(this._holdEnableDisableReasonSpin);
    }

    protected onFreeSpinsIntroFinished(): void {
        this.view.initFreeSpins();
    }

    protected onFreeSpinsRestored(): void {
        this.view.initFreeSpins();
    }

    protected onRespinRestored(): void {
        this.onRespinStarted(true);
    }

    protected onRespinStarted(isStarted: boolean): void {
        isStarted && this.disableHoldDuringRespin();
    }

    protected disableHoldDuringRespin(): void {
        if (this.logic.isBonusOnFreeGames(Bonus.RESPIN_BONUS)) {
            this.view.initFreeSpins();
        }
    }

    protected onInitialWinAnimationStarted(_listenerID: symbol, _pays: Pay[], _isBlocking: boolean): void {
        // isBlocking && this.view.disableSpinButton();
    }

    protected onSpinButtonClicked(): void {
        this.currentState.handle(SpinButtonViewEvents.SPIN_BUTTON_CLICKED);
        this.view.spinButtonClickAction();
    }

    protected onSpaceBarUp(): void {
        if (this.view.isSpinButtonEnabled()) {
            this.soundPlayer.play('spinclick');
            this.currentState.handle(SpinButtonViewEvents.SPIN_BUTTON_CLICKED);
        }
    }

    protected onSpinsLeftButtonCLicked(): void {
        this.currentState.handle(SpinButtonViewMobileLandscape.SPINS_LEFT_BUTTON_CLICKED);
    }

    protected onSpinButtonHold(): void {
        this.eventManager.emit(SpinButtonEvents.SPIN_BUTTON_HOLD);
    }

    protected onAutoplayStarted(): void {
        this.view.initAutoplay();
        this.disableButton(UIDisableReason.AUTOPLAY_MODE);
    }

    protected onAutoplayStopped(): void {
        this.view.uninitAutoplay();
        this.enableButton(UIDisableReason.AUTOPLAY_MODE);
    }

    protected onSpinResponse(): void {
        this.onAnySpinResponse();
        this.currentState.handle(LogicEvents.SPIN_RESPONSE);
    }

    protected onRespinResponse(): void {
        this.onAnySpinResponse();
        this.currentState.handle(LogicEvents.SPIN_RESPONSE);
    }

    protected onMiniSpinResponse(): void {
        this.onAnySpinResponse();
        this.currentState.handle(LogicEvents.SPIN_RESPONSE);
    }

    protected onFreeSpinResponse(): void {
        this.onAnySpinResponse();
        this.currentState.handle(LogicEvents.FREE_SPIN_RESPONSE);
    }

    protected onAnySpinResponse(): void {
        this.enableButton(UIDisableReason.SPIN_RESPONSE);
    }

    public get disableHoldSettings(): boolean {
        return this._disabledHoldSettings;
    }
}
